/**
 * addressParts — split-address mortgagee form (operator design 2026-08-03).
 *
 * The form renders the mailing address as four inputs (Street / City / State /
 * ZIP) instead of one free-text line, then composes the canonical
 * `"<street>, <city>, <ST> <zip>"` string on submit. That shape matches the
 * workflow's regex parser BY CONSTRUCTION, so a form-launched address can
 * never fall through to the mid-run LLM parse (the path that cleared a live
 * record's address on instance 9952ac59). The wire contract is unchanged —
 * `mortgagees[i].mailing_address` stays a single string; the part keys are a
 * prefill/UI channel only and are stripped before submit.
 *
 * Python composes the same way server-side after extraction
 * (workflows `_mortgagee_address_parts.py`) — keep the two in lock-step.
 */

import { actionOf } from "@product-app/common/workflows/launch/actionSlice";

// The workflow parser's regex (workflows/rpa/_address_parse.py), ported:
// street may contain commas (non-greedy), city may not, 2-letter state,
// ZIP or ZIP+4 at the end.
const ADDRESS_RE = /^\s*(.+?),\s*([^,]+),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/;
export const ZIP_RE = /^\d{5}(-\d{4})?$/;

export const ADDRESS_PART_KEYS = [
  "mailing_street",
  "mailing_city",
  "mailing_state",
  "mailing_zip",
] as const;
export type AddressPartKey = (typeof ADDRESS_PART_KEYS)[number];

export interface AddressParts {
  mailing_street: string;
  mailing_city: string;
  mailing_state: string;
  mailing_zip: string;
}

export interface UsState {
  code: string;
  name: string;
}

/** USPS state/territory codes — what Epic's ADDREDIT state field accepts. */
export const US_STATES: readonly UsState[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "AS", name: "American Samoa" },
  { code: "GU", name: "Guam" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "PR", name: "Puerto Rico" },
  { code: "VI", name: "U.S. Virgin Islands" },
  { code: "AA", name: "Armed Forces Americas" },
  { code: "AE", name: "Armed Forces Europe" },
  { code: "AP", name: "Armed Forces Pacific" },
];

const STATE_BY_CODE = new Map(US_STATES.map((s) => [s.code, s]));

/** Display form the operator asked for: `"Michigan | MI"`. */
export function stateLabel(s: UsState): string {
  return `${s.name} | ${s.code}`;
}

/** Type-to-filter: substring on the name, prefix on the code, both from the
 *  combined label — `"mich"`, `"MI"`, and `"Michigan | M"` all hit Michigan. */
export function filterStates(query: string): UsState[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...US_STATES];
  return US_STATES.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.code.toLowerCase().startsWith(q) ||
      stateLabel(s).toLowerCase().includes(q),
  );
}

/** Resolve free text back to a state for blur-commit: exact code, exact name,
 *  or the exact label — never a fuzzy guess. */
export function matchState(text: string): UsState | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  return (
    US_STATES.find(
      (s) =>
        s.code.toLowerCase() === t ||
        s.name.toLowerCase() === t ||
        stateLabel(s).toLowerCase() === t,
    ) ?? null
  );
}

function clean(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * The interest-record key these helpers assume when the caller does not name
 * one. Every existing call site (the FLAT mortgagee branch in `App.tsx`, and
 * the `actions[0].mortgagees` shape `action-list-contract` landed) uses it, so
 * defaulting keeps them compiling and behaving byte-identically.
 */
const DEFAULT_RECORDS_KEY = "mortgagees";

/**
 * Where the interest-record list lives in a value object.
 *
 * `recordsKey` names the key WITHIN the record this function is handed — the
 * reshaped contract's declared records key, which `buildLaunchBinding` resolves
 * from a candidate list (`["interests", "mortgagees"]`). It is NOT a path: the
 * function still looks in the same two places it always did.
 *
 * The v2 launch contract moved the record list (with the rest of the
 * per-action values) inside `actions[0]`, so the nested read goes through
 * `actionOf()`. The FLAT read stays as the fallback and is not dead code: the
 * pre-reshape shape is still what a legacy fixture, a legacy prefill and any
 * non-mortgagee caller produce, and returning the input unchanged for those is
 * what keeps this module's existing behaviour byte-identical.
 *
 * `nested` records WHERE the list was found so the write-back lands in the same
 * place — reading flat and writing nested would silently relocate the AM's rows
 * to a level the source never had.
 *
 * A `recordsKey` the given record does not carry returns `{ list: null }` and
 * every caller then returns its input unchanged: these functions stay TOTAL.
 * The loudness lives one level up — `binding.roles.interestRecords` resolving to
 * null is a `missingRoles` entry, and the AC-6 round-trip probe asserts the
 * composed address, so a mis-resolved key fails a test rather than passing.
 */
function locateMortgagees(
  values: Record<string, unknown>,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): {
  list: unknown[] | null;
  nested: boolean;
} {
  const fromAction = actionOf(values)[recordsKey];
  if (Array.isArray(fromAction)) return { list: fromAction, nested: true };
  const flat = values[recordsKey];
  if (Array.isArray(flat)) return { list: flat, nested: false };
  return { list: null, nested: false };
}

/** Write the record list back where `locateMortgagees` found it. */
function withMortgagees(
  values: Record<string, unknown>,
  next: unknown[],
  nested: boolean,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): Record<string, unknown> {
  if (!nested) return { ...values, [recordsKey]: next };
  const actions = Array.isArray(values.actions) ? [...values.actions] : [{}];
  const first = actionOf(values);
  actions[0] = { ...first, [recordsKey]: next };
  return { ...values, actions };
}

/** Split one canonical/comma-form line into parts. Null when the shape
 *  doesn't match — the caller decides the fallback. */
export function splitMailingAddress(text: string): AddressParts | null {
  const m = ADDRESS_RE.exec(text);
  if (!m) return null;
  return {
    mailing_street: m[1].trim(),
    mailing_city: m[2].trim(),
    mailing_state: m[3],
    mailing_zip: m[4],
  };
}

/** Read a mortgagee item's parts, string-typed and trimmed. */
export function readParts(item: Record<string, unknown>): AddressParts {
  return {
    mailing_street: clean(item.mailing_street),
    mailing_city: clean(item.mailing_city),
    mailing_state: clean(item.mailing_state).toUpperCase(),
    mailing_zip: clean(item.mailing_zip),
  };
}

/** All-or-nothing + shape check for one row's parts. */
export function partsError(p: AddressParts): string | null {
  const filled = ADDRESS_PART_KEYS.filter((k) => p[k] !== "");
  if (filled.length === 0) return null; // deliberately blank address is valid
  const missing: string[] = [];
  if (!p.mailing_street) missing.push("Street");
  if (!p.mailing_city) missing.push("City");
  if (!p.mailing_state) missing.push("State");
  if (!p.mailing_zip) missing.push("ZIP");
  if (missing.length > 0) {
    return `address needs ${missing.join(", ")} — fill the missing part(s) or clear all four`;
  }
  if (!STATE_BY_CODE.has(p.mailing_state)) {
    return "pick the State from the list";
  }
  if (!ZIP_RE.test(p.mailing_zip)) {
    return "ZIP must be 5 digits or ZIP+4 (e.g. 48007 or 48007-1234)";
  }
  return null;
}

/** Compose the canonical string from four VALID parts, else null.
 *  Mirrors workflows `_mortgagee_address_parts.compose_mailing_address`. */
export function composeMailingAddress(p: AddressParts): string | null {
  const street = p.mailing_street;
  const city = p.mailing_city.replace(/,/g, " ").replace(/\s{2,}/g, " ").trim();
  const state = p.mailing_state;
  const zip = p.mailing_zip;
  if (!street || !city || !state || !zip) return null;
  if (!STATE_BY_CODE.has(state)) return null;
  if (!ZIP_RE.test(zip)) return null;
  return `${street}, ${city}, ${state} ${zip}`;
}

/**
 * Prefill → form: make sure every mortgagee item carries the four part keys.
 * Server-emitted parts win; a legacy single-string `mailing_address` is split
 * client-side; a string that won't split lands whole in Street with the rest
 * blank — the AM completes it, which is the entire point of the design.
 */
export function decomposeMortgageePrefill(
  prefill: Record<string, unknown>,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): Record<string, unknown> {
  const { list: mortgagees, nested } = locateMortgagees(prefill, recordsKey);
  if (!mortgagees) return prefill;
  const next = mortgagees.map((raw) => {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return raw;
    const item = raw as Record<string, unknown>;
    const parts = readParts(item);
    const hasAnyPart = ADDRESS_PART_KEYS.some((k) => parts[k] !== "");
    if (!hasAnyPart) {
      const addr = clean(item.mailing_address);
      if (addr) {
        const split = splitMailingAddress(addr);
        if (split) {
          return { ...item, ...split };
        }
        return { ...item, ...parts, mailing_street: addr };
      }
    }
    return { ...item, ...parts };
  });
  return withMortgagees(prefill, next, nested, recordsKey);
}

/**
 * Form → wire: compose `mailing_address` per row and STRIP the part keys.
 * All-empty parts → `mailing_address: ""` (a deliberately blank address —
 * existing launch semantics). A partial row should be unreachable (validation
 * blocks submit); if one slips through, the raw space-join is sent and the
 * server's trailing-ZIP launch gate rejects it loudly rather than silently.
 */
export function composeMortgageeValues(
  values: Record<string, unknown>,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): Record<string, unknown> {
  const { list: mortgagees, nested } = locateMortgagees(values, recordsKey);
  if (!mortgagees) return values;
  const next = mortgagees.map((raw) => {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return raw;
    const item = raw as Record<string, unknown>;
    const parts = readParts(item);
    const rest: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(item)) {
      if (!(ADDRESS_PART_KEYS as readonly string[]).includes(k)) rest[k] = v;
    }
    const hasAnyPart = ADDRESS_PART_KEYS.some((k) => parts[k] !== "");
    if (!hasAnyPart) {
      return { ...rest, mailing_address: "" };
    }
    const composed = composeMailingAddress(parts);
    if (composed !== null) {
      return { ...rest, mailing_address: composed };
    }
    const bestEffort = [
      parts.mailing_street,
      parts.mailing_city,
      parts.mailing_state,
      parts.mailing_zip,
    ]
      .filter(Boolean)
      .join(" ");
    return { ...rest, mailing_address: bestEffort };
  });
  return withMortgagees(values, next, nested, recordsKey);
}

/** Form-level error for `fieldErrors[recordsKey]` — first offending row wins. */
export function mortgageeAddressError(
  values: Record<string, unknown>,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): string | null {
  const { list: mortgagees } = locateMortgagees(values, recordsKey);
  if (!mortgagees) return null;
  for (let i = 0; i < mortgagees.length; i++) {
    const raw = mortgagees[i];
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) continue;
    const err = partsError(readParts(raw as Record<string, unknown>));
    if (err) {
      return `Mortgagee ${i + 1}: ${err}`;
    }
  }
  return null;
}

// Rewrite results are cached per input schema object: the caller sits in a
// render-prop that re-runs on every keystroke, and a fresh clone each time
// would defeat JsonSchemaForm's useMemo(parseObjectSchema, [schema]).
//
// Keyed on the schema object AND the records key. The same schema object
// rewritten for two different record keys must not return the first rewrite —
// with a schema-only key, a form that resolved `interests` after something had
// already split the same object under `mortgagees` would silently render the
// wrong record's address inputs.
const splitSchemaCache = new WeakMap<
  object,
  Map<string, Record<string, unknown>>
>();

/**
 * Presentation-only schema rewrite: replace the mortgagee item's single
 * `mailing_address` property with the four part properties, IN PLACE in the
 * property order (so the inputs render where the address rendered). The
 * original schema object is not mutated. Returns the input unchanged when
 * the expected shape isn't found — the form then renders exactly as today.
 */
function derefInto(
  clone: Record<string, unknown>,
  node: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!node) return undefined;
  if (typeof node.$ref !== "string") return node;
  // Resolve "#/$defs/MortgageeRecord" INTO THE CLONE so the caller mutates the
  // clone's own node and never the input schema.
  const path = (node.$ref as string).replace(/^#\//, "").split("/");
  let cur: unknown = clone;
  for (const seg of path) {
    cur = (cur as Record<string, unknown> | undefined)?.[seg];
  }
  return cur as Record<string, unknown> | undefined;
}

/**
 * Find the mortgagees ARRAY node and the MortgageeRecord node it points at,
 * in the clone. Three shapes, in precedence order:
 *
 *  1. `properties.mortgagees` — the pre-reshape FLAT schema. Kept FIRST so a
 *     non-nested schema splits byte-identically to today.
 *  2. `properties.actions.items` → `properties.mortgagees` — the v2 launch
 *     contract, where the mortgagee list lives inside the action entry.
 *  3. `$defs.MortgageeRecord` directly — the last-resort shape lookup, for a
 *     schema whose array node this function cannot name.
 *
 * Returns the array node separately from the record node because the two carry
 * DIFFERENT annotations: `x-item-label` belongs on the ARRAY (it labels the row
 * card), and the split properties belong on the RECORD.
 */
function locateMortgageeSchemaNodes(
  clone: Record<string, unknown>,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): {
  arrayNode?: Record<string, unknown>;
  recordNode?: Record<string, unknown>;
} {
  const props = clone.properties as Record<string, unknown> | undefined;

  const flat = props?.[recordsKey] as Record<string, unknown> | undefined;
  if (flat) {
    return {
      arrayNode: flat,
      recordNode: derefInto(clone, flat.items as Record<string, unknown> | undefined),
    };
  }

  const actions = props?.actions as Record<string, unknown> | undefined;
  const actionItem = derefInto(
    clone,
    actions?.items as Record<string, unknown> | undefined,
  );
  const nested = (actionItem?.properties as Record<string, unknown> | undefined)
    ?.[recordsKey] as Record<string, unknown> | undefined;
  if (nested) {
    return {
      arrayNode: nested,
      recordNode: derefInto(
        clone,
        nested.items as Record<string, unknown> | undefined,
      ),
    };
  }

  const defs = clone.$defs as Record<string, unknown> | undefined;
  const record = defs?.MortgageeRecord as Record<string, unknown> | undefined;
  if (record) {
    const action = defs?.MortgageeAction as Record<string, unknown> | undefined;
    const arrayNode = (action?.properties as Record<string, unknown> | undefined)
      ?.[recordsKey] as Record<string, unknown> | undefined;
    return { arrayNode, recordNode: record };
  }

  return {};
}

export function splitAddressSchema(
  schema: Record<string, unknown> | null | undefined,
  recordsKey: string = DEFAULT_RECORDS_KEY,
): Record<string, unknown> | null | undefined {
  if (!schema) return schema;
  const byKey = splitSchemaCache.get(schema);
  const cached = byKey?.get(recordsKey);
  if (cached) return cached;
  const clone = JSON.parse(JSON.stringify(schema)) as Record<string, unknown>;
  const { arrayNode: mortgagees, recordNode: items } =
    locateMortgageeSchemaNodes(clone, recordsKey);
  const itemProps = items?.properties as Record<string, unknown> | undefined;
  if (!itemProps || !("mailing_address" in itemProps)) return schema;
  const rebuilt: Record<string, unknown> = {};
  for (const [key, def] of Object.entries(itemProps)) {
    if (key !== "mailing_address") {
      rebuilt[key] = def;
      continue;
    }
    rebuilt.mailing_street = { type: "string", title: "Street" };
    rebuilt.mailing_city = { type: "string", title: "City" };
    rebuilt.mailing_state = { type: "string", title: "State" };
    rebuilt.mailing_zip = { type: "string", title: "ZIP" };
  }
  (items as Record<string, unknown>).properties = rebuilt;
  // Card header: "New Mortgagee" instead of "Mortgagee 1" (operator request
  // 2026-08-03). Delete hides the whole section, so the label only ever
  // shows on Add/Change — where the row IS the new mortgagee. The row index
  // still appears when a second row exists (multi-mortgagee emails).
  //
  // On the ARRAY node, never the record def: `parseObjectSchema` reads
  // `x-item-label` off the array property, and the record def is shared by
  // every array that $refs it.
  if (mortgagees) mortgagees["x-item-label"] = "New Mortgagee";
  if (byKey) {
    byKey.set(recordsKey, clone);
  } else {
    splitSchemaCache.set(schema, new Map([[recordsKey, clone]]));
  }
  return clone;
}
